import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PhoneInTalk } from "@mui/icons-material";

import svenImage from "src/shared/assets/sven.webp";
import { handleBookCall } from "src/shared/utils";

export const MainInfo: React.FC = () => {
  const theme = useTheme();

  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack
      width={1}
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{
        gap: { lg: 8, md: 1, sm: 1, xs: 1 },
      }}
    >
      <Stack
        width={1}
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={1}
      >
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Stack
            width={1}
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Typography
              variant="h1"
              color="primary.contrastText"
              whiteSpace="nowrap"
              sx={{
                fontSize: {
                  xl: "5rem",
                  lg: "4.5rem",
                  md: "4rem",
                  sm: "3rem",
                  xs: "2rem",
                },
              }}
            >
              Welcome, I am
            </Typography>

            <Typography
              variant="h1"
              color="primary"
              whiteSpace="nowrap"
              sx={{
                fontSize: {
                  xl: "5rem",
                  lg: "4.5rem",
                  md: "4rem",
                  sm: "3rem",
                  xs: "2rem",
                },
              }}
            >
              Sven Badalyan
            </Typography>
          </Stack>

          <Stack
            width={0.8}
            direction="row"
            justifyContent="center"
            alignItems="center"
            position="relative"
            sx={{
              display: { sm: "none", xs: "flex" },
              aspectRatio: 1,
            }}
          >
            <Box
              bgcolor="secondary.main"
              borderRadius="50%"
              position="absolute"
              width={0.9}
              height={0.9}
              top={0}
            />

            <img
              width="100%"
              height="100%"
              src={svenImage}
              alt="Sven Badalyan"
              style={{
                objectFit: "contain",
                zIndex: 1,
              }}
            />
          </Stack>
        </Stack>

        <Typography
          color="text.primary"
          variant="body2"
          sx={{
            fontSize: { lg: "0.875rem", xs: "0.7rem" },
            textAlign: { sm: "left", xs: "center" },
          }}
        >
          Your internal team is strong. But your most critical AI initiative—the
          one that cannot fail—demands a different kind of force. I bring strong
          R&D expertise to solve the complex AI problems delivering
          mission-critical AI systems with the speed of a catalyst and the
          precision of a master craftsman.
        </Typography>

        <Stack
          width={1}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography
              variant="h2"
              color="textSecondary"
              sx={{
                textShadow: "1px 1px 20px rgba(81, 139, 241, 1);",
                fontSize: {
                  lg: "3.75rem",
                  md: "3rem",
                  sm: "2.5rem",
                  xs: "2rem",
                },
              }}
            >
              12
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              whiteSpace="nowrap"
              fontWeight={300}
              sx={{
                fontSize: {
                  lg: "0.875rem",
                  md: "0.7rem",
                  sm: "0.7rem",
                  xs: "0.6rem",
                },
              }}
            >
              years AI Expert
            </Typography>
          </Stack>

          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography
              variant="h2"
              color="textSecondary"
              sx={{
                textShadow: "1px 1px 20px rgba(81, 139, 241, 1);",
                fontSize: {
                  lg: "3.75rem",
                  md: "3rem",
                  sm: "2.5rem",
                  xs: "2rem",
                },
              }}
            >
              30+
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              whiteSpace="nowrap"
              fontWeight={300}
              sx={{
                fontSize: {
                  lg: "0.875rem",
                  md: "0.7rem",
                  sm: "0.7rem",
                  xs: "0.6rem",
                },
              }}
            >
              industrial projects in AI
            </Typography>
          </Stack>

          <Stack direction="column" justifyContent="center" alignItems="center">
            <Typography
              variant="h2"
              color="textSecondary"
              sx={{
                textShadow: "1px 1px 20px rgba(81, 139, 241, 1);",
                fontSize: {
                  lg: "3.75rem",
                  md: "3rem",
                  sm: "2.5rem",
                  xs: "2rem",
                },
              }}
            >
              &gt;95%
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              whiteSpace="nowrap"
              fontWeight={300}
              sx={{
                fontSize: {
                  lg: "0.875rem",
                  md: "0.7rem",
                  sm: "0.7rem",
                  xs: "0.6rem",
                },
              }}
            >
              MVP Adoption Rate
            </Typography>
          </Stack>
        </Stack>

        <Button
          variant="outlined"
          color="primary"
          size={isMedium ? "medium" : isSmall ? "small" : "large"}
          startIcon={<PhoneInTalk fontSize="large" color="primary" />}
          onClick={handleBookCall}
          sx={{
            textTransform: "none",
            color: "primary.contrastText",
            gap: 1,
            marginTop: 2,
          }}
        >
          Book a Call With
          <Typography color="primary">Sven Badalyan</Typography>
        </Button>
      </Stack>

      <Stack
        width={1}
        direction="row"
        justifyContent="center"
        alignItems="center"
        position="relative"
        sx={{
          display: { sm: "flex", xs: "none" },
          aspectRatio: 1,
        }}
      >
        <Box
          bgcolor="secondary.main"
          borderRadius="50%"
          position="absolute"
          width={0.9}
          height={0.9}
          top={0}
        />

        <img
          width="100%"
          height="100%"
          src={svenImage}
          alt="Sven Badalyan"
          style={{
            objectFit: "contain",
            zIndex: 1,
          }}
        />
      </Stack>
    </Stack>
  );
};
